import type { ModelsUser } from '@/api/generated-api'
import {
  isValidProfileBirthDate,
  PROFILE_BIRTH_DATE_FORMAT
} from '@/utils/profileBirthDate'

export type FanProfileMissingField =
  | 'favoriteClub'
  | 'birthDate'
  | 'gender'
  | 'location'

export const FAN_PROFILE_TASK_FIELDS: readonly FanProfileMissingField[] = [
  'favoriteClub',
  'birthDate',
  'gender',
  'location'
] as const

export type FanProfileCompletenessInput = Pick<
  ModelsUser,
  'favorite_club_id' | 'waitlist_club_id' | 'birth_date' | 'country' | 'city'
> & {
  gender?: string | null
}

function isNonEmpty(value?: string | null): boolean {
  return Boolean(value?.trim())
}

export function hasFanClubSelection(
  profile: FanProfileCompletenessInput | null | undefined
): boolean {
  if (!profile) {
    return false
  }
  return (
    isNonEmpty(profile.favorite_club_id) || isNonEmpty(profile.waitlist_club_id)
  )
}

function hasValidBirthDate(birthDate?: string | null): boolean {
  const value = birthDate?.trim() ?? ''
  if (!value) {
    return false
  }
  if (!PROFILE_BIRTH_DATE_FORMAT.test(value)) {
    return false
  }
  return isValidProfileBirthDate(value)
}

function hasLocation(profile: FanProfileCompletenessInput): boolean {
  return isNonEmpty(profile.country)
}

function hasGender(profile: FanProfileCompletenessInput): boolean {
  return isNonEmpty(profile.gender)
}

export function resolveMissingFanProfileFields(
  profile: FanProfileCompletenessInput | null | undefined
): FanProfileMissingField[] {
  if (!profile) {
    return [...FAN_PROFILE_TASK_FIELDS]
  }

  const missing: FanProfileMissingField[] = []

  if (!hasFanClubSelection(profile)) {
    missing.push('favoriteClub')
  }

  if (!hasValidBirthDate(profile.birth_date)) {
    missing.push('birthDate')
  }

  if (!hasGender(profile)) {
    missing.push('gender')
  }

  if (!hasLocation(profile)) {
    missing.push('location')
  }

  return missing
}

export function isFanProfileCompleteForTasks(
  profile: FanProfileCompletenessInput | null | undefined
): boolean {
  return resolveMissingFanProfileFields(profile).length === 0
}
